import { type SuretyAccountId, type SuretyQuoteId, SuretyQuoteStatus } from "@inrev/common";
import { useCallback, useRef } from "react";
import {
	type BondRequestFlowEntry,
	type BondRequestFlowOutcome,
	type BondRequestFlowSurface,
	type BondRequestWaitResolution,
	trackBondRequestFlowAbandoned,
	trackBondRequestFlowCompleted,
	trackBondRequestFlowEntered,
	trackBondRequestFlowFailed,
	trackBondRequestOutcomeShown,
	trackBondRequestWaitResolved,
} from "../../analytics";
import { useAbandonOnUnmount } from "./useAbandonOnUnmount";

/**
 * The screen a freshly created request lands the user on. Statuses a creation
 * can't produce have no outcome screen, so they report nothing rather than
 * being folded into a neighbouring outcome.
 */
export const bondRequestOutcomeFromStatus = (
	status: SuretyQuoteStatus,
): BondRequestFlowOutcome | undefined => {
	switch (status) {
		case SuretyQuoteStatus.approved:
			return "approved";
		case SuretyQuoteStatus.review:
			return "review";
		case SuretyQuoteStatus.declined:
			return "declined";
		default:
			return undefined;
	}
};

/**
 * Owns the Entered/Completed/Failed/Abandoned lifecycle for one bond request
 * submission attempt, so every surface reports the same funnel.
 *
 * Two rules the call sites depend on:
 * - A failed submit is *not* terminal. The user stays on the form, so a later
 *   success still reports a completion.
 * - Once the bond request exists the flow has converted, even if the user never
 *   sees the underwriting outcome. Abandonment is only reported before that.
 *
 * Abandonment fires on unmount, which covers closing a modal, navigating away
 * from the page, and bouncing off an untouched form -- the navigation blocker
 * only triggers on a dirty form and would miss the last case entirely.
 *
 * Closing out of the post-submit wait is therefore *not* an abandonment: the
 * request exists, extraction is running, and the outcome reaches the user by
 * toast either way. That choice is its own event -- see `onWaitResolved`.
 */
export const useBondRequestFlowAnalytics = (entry: BondRequestFlowEntry) => {
	const accountIdRef = useRef<SuretyAccountId | undefined>(undefined);
	const enteredAccountIdRef = useRef<SuretyAccountId | undefined>(undefined);
	const outcomeShownRef = useRef(false);
	const convertedRef = useRef(false);
	const abandonedRef = useRef(false);
	const waitingRequestIdRef = useRef<SuretyQuoteId | undefined>(undefined);
	const waitResolvedRef = useRef(false);
	const waitStartedAtRef = useRef<number | undefined>(undefined);
	const surfaceRef = useRef<BondRequestFlowSurface>(entry);

	const flowContext = useCallback(
		(accountId: SuretyAccountId) => ({ accountId, entry, surface: surfaceRef.current }),
		[entry],
	);

	/**
	 * Called when an admin escalates a quick submit to the full modal. `entry`
	 * keeps recording where the flow started; `surface` follows the user.
	 */
	const markEscalatedToFullForm = useCallback(() => {
		surfaceRef.current = "full";
	}, []);

	// Re-reports when an admin switches accounts mid-flow, so Entered and
	// Completed always agree on `accountId`. The account left behind is
	// abandoned first -- otherwise it would linger as a funnel entry the user
	// explicitly moved away from and never converted.
	const onEntered = useCallback(
		(accountId: SuretyAccountId) => {
			accountIdRef.current = accountId;
			if (enteredAccountIdRef.current === accountId || convertedRef.current) return;
			const previousAccountId = enteredAccountIdRef.current;
			if (previousAccountId !== undefined && !abandonedRef.current) {
				trackBondRequestFlowAbandoned(flowContext(previousAccountId));
			}
			enteredAccountIdRef.current = accountId;
			trackBondRequestFlowEntered(flowContext(accountId));
		},
		[flowContext],
	);

	const onCreated = useCallback(
		(accountId: SuretyAccountId, bondRequestId: SuretyQuoteId) => {
			accountIdRef.current = accountId;
			if (convertedRef.current) return;
			convertedRef.current = true;
			trackBondRequestFlowCompleted({ ...flowContext(accountId), bondRequestId });
		},
		[flowContext],
	);

	const onOutcomeShown = useCallback(
		(accountId: SuretyAccountId, bondRequestId: SuretyQuoteId, outcome: BondRequestFlowOutcome) => {
			if (outcomeShownRef.current) return;
			outcomeShownRef.current = true;
			trackBondRequestOutcomeShown({ ...flowContext(accountId), bondRequestId, outcome });
		},
		[flowContext],
	);

	/**
	 * Whether the user sat through extraction/underwriting or closed out and left
	 * it to the toast, and how long the wait ran either way. Only meaningful once
	 * the flow reached the waiting step, and only the first resolution counts --
	 * backgrounding unmounts the flow, so a stale `waited` can never follow it.
	 */
	const onWaitResolved = useCallback(
		(resolution: BondRequestWaitResolution) => {
			const accountId = accountIdRef.current;
			const bondRequestId = waitingRequestIdRef.current;
			const startedAt = waitStartedAtRef.current;
			if (!accountId || !bondRequestId || startedAt === undefined || waitResolvedRef.current) {
				return;
			}
			waitResolvedRef.current = true;
			trackBondRequestWaitResolved({
				...flowContext(accountId),
				bondRequestId,
				resolution,
				waitDurationSeconds: Math.round((performance.now() - startedAt) / 1000),
			});
		},
		[flowContext],
	);

	/**
	 * Arms `onWaitResolved` and starts its clock. The flow reports nothing if it
	 * never gets here. Called on every render of the waiting step, so the first
	 * stamp wins -- re-stamping would reset the duration mid-wait.
	 */
	const onWaitingStarted = useCallback((bondRequestId: SuretyQuoteId) => {
		if (waitStartedAtRef.current !== undefined) return;
		waitingRequestIdRef.current = bondRequestId;
		waitStartedAtRef.current = performance.now();
	}, []);

	const onFailed = useCallback(
		(accountId: SuretyAccountId, errorName: string) => {
			accountIdRef.current = accountId;
			trackBondRequestFlowFailed({ ...flowContext(accountId), errorName });
		},
		[flowContext],
	);

	const onAbandoned = useCallback(() => {
		const accountId = accountIdRef.current;
		if (!enteredAccountIdRef.current || convertedRef.current || abandonedRef.current || !accountId)
			return;
		abandonedRef.current = true;
		trackBondRequestFlowAbandoned(flowContext(accountId));
	}, [flowContext]);

	useAbandonOnUnmount(onAbandoned);

	return {
		markEscalatedToFullForm,
		onEntered,
		onCreated,
		onOutcomeShown,
		onWaitingStarted,
		onWaitResolved,
		onFailed,
		onAbandoned,
	};
};

export type BondRequestFlowAnalytics = ReturnType<typeof useBondRequestFlowAnalytics>;
